var Popup_1;
import { __decorate } from "tslib";
import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import omit from 'lodash/omit';
import shallowEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import isElement from 'lodash/isElement';
import ViewComponent from 'choerodon-ui/pro/lib/core/ViewComponent';
import autobind from 'choerodon-ui/pro/lib/_util/autobind';
import { findFocusableElements } from 'choerodon-ui/pro/lib/_util/focusable';
import { getDocument } from 'choerodon-ui/pro/lib/_util/DocumentUtils';
import Align from '../align';
import { getProPrefixCls } from '../configure';
import Animate from '../animate';
import PopupInner from './PopupInner';
/**
 * 记录ID生成器
 */
const PopupKeyGen = (function* (start) {
    while (true) {
        yield `popup-key-${start++}`;
    }
})(1);
const childrenProps = { hidden: 'hidden' };
function newPopupContainer() {
    const doc = getDocument(window);
    const popupContainer = doc.createElement('div');
    popupContainer.className = getProPrefixCls('popup-container');
    return popupContainer;
}
let Popup = Popup_1 = class Popup extends ViewComponent {
    constructor() {
        super(...arguments);
        this.contentRendered = false;
        this.popupKey = PopupKeyGen.next().value;
        this.saveRef = align => (this.align = align);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'align',
            'transitionName',
            'getRootDomNode',
            'getPopupContainer',
            'getClassNameFromAlign',
            'getStyleFromAlign',
            'onAlign',
            'onAnimateAppear',
            'onAnimateEnter',
            'onAnimateLeave',
            'onAnimateEnd',
            'getFocusableElements',
        ]);
    }
    componentWillUnmount() {
        const { popupContainer } = this;
        if (popupContainer && popupContainer !== Popup_1.popupContainer && popupContainer.parentNode) {
            popupContainer.parentNode.removeChild(popupContainer);
        }
    }
    componentDidUpdate() {
        this.findFocusableElements();
    }
    componentDidMount() {
        super.componentDidMount();
        this.findFocusableElements();
    }
    findFocusableElements() {
        const { element } = this;
        const { getFocusableElements } = this.props;
        if (element && getFocusableElements) {
            const elements = findFocusableElements(element);
            getFocusableElements(elements && elements.filter(item => item.tabIndex !== -1).sort((e1, e2) => e1.tabIndex - e2.tabIndex));
        }
    }
    renderInner(innerRef) {
        const { children, getClassNameFromAlign = noop, align } = this.props;
        const className = this.getMergedClassNames(this.currentAlignClassName ||
            getClassNameFromAlign(align));
        return (React.createElement(PopupInner, Object.assign({}, omit(this.getMergedProps(), ['ref', 'className']), { className: className, innerRef: innerRef }), children));
    }
    render() {
        const { hidden, align, transitionName, getRootDomNode, onAnimateAppear = noop, onAnimateEnter = noop, onAnimateLeave = noop, onAnimateEnd = noop, } = this.props;
        if (!hidden) {
            this.contentRendered = true;
        }
        const container = this.getContainer();
        return container && this.contentRendered ? createPortal(React.createElement(Animate, { component: "", exclusive: true, transitionAppear: true, transitionName: transitionName, hiddenProp: "hidden", onAppear: onAnimateAppear, onEnter: onAnimateEnter, onLeave: onAnimateLeave, onEnd: onAnimateEnd },
            React.createElement(Align, { ref: this.saveRef, childrenRef: this.elementReference, key: "align", childrenProps: childrenProps, align: align, onAlign: this.onAlign, target: getRootDomNode, hidden: hidden, monitorWindowResize: true }, this.renderInner)), container, this.popupKey) : null;
    }
    getContainer() {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const { getPopupContainer, getRootDomNode = noop } = this.props;
        const globalContainer = Popup_1.popupContainer;
        if (getPopupContainer) {
            const container = this.popupContainer;
            if (container) {
                return container;
            }
        }
        else if (globalContainer) {
            return globalContainer;
        }
        if (getPopupContainer) {
            const mountNode = getPopupContainer(getRootDomNode());
            const popupContainer = newPopupContainer();
            const root = window.document.body;
            if (mountNode === root) {
                if (globalContainer) {
                    this.popupContainer = globalContainer;
                    return globalContainer;
                }
                Popup_1.popupContainer = popupContainer;
            }
            (isElement(mountNode) ? mountNode : root).appendChild(popupContainer);
            this.popupContainer = popupContainer;
            return popupContainer;
        }
        // eslint-disable-next-line no-use-before-define
        return getGlobalPopupContainer();
    }
    onAlign(source, align, target, translate) {
        const { getClassNameFromAlign = noop, getStyleFromAlign = noop, onAlign = noop } = this.props;
        const currentAlignClassName = getClassNameFromAlign(align);
        const differentTarget = target !== this.target;
        if (differentTarget || this.currentAlignClassName !== currentAlignClassName) {
            this.currentAlignClassName = currentAlignClassName;
            source.className = this.getMergedClassNames(currentAlignClassName);
        }
        const currentAlignStyle = getStyleFromAlign(target, align);
        if (differentTarget || !shallowEqual(this.currentAlignStyle, currentAlignStyle)) {
            this.currentAlignStyle = currentAlignStyle;
            Object.assign(source.style, currentAlignStyle);
        }
        onAlign(source, align, target, translate);
        this.target = source;
    }
    forceAlign() {
        if (this.align) {
            this.align.forceAlign();
        }
    }
};
Popup.displayName = 'Popup';
Popup.propTypes = {
    align: PropTypes.object,
    onAlign: PropTypes.func,
    getRootDomNode: PropTypes.func,
    getPopupContainer: PropTypes.func,
    transitionName: PropTypes.string,
    onAnimateAppear: PropTypes.func,
    onAnimateEnter: PropTypes.func,
    onAnimateLeave: PropTypes.func,
    onAnimateEnd: PropTypes.func,
    getStyleFromAlign: PropTypes.func,
    getClassNameFromAlign: PropTypes.func,
    ...ViewComponent.propTypes,
};
Popup.defaultProps = {
    suffixCls: 'popup',
    transitionName: 'zoom',
};
__decorate([
    autobind
], Popup.prototype, "renderInner", null);
__decorate([
    autobind
], Popup.prototype, "onAlign", null);
Popup = Popup_1 = __decorate([
    observer
], Popup);
export default Popup;
export function getGlobalPopupContainer() {
    if (Popup.popupContainer) {
        return Popup.popupContainer;
    }
    const popupContainer = newPopupContainer();
    const root = getDocument(window).body;
    root.appendChild(popupContainer);
    Popup.popupContainer = popupContainer;
    return popupContainer;
}
